import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { Button, LoaderSpinner } from 'components/UtilElements';
import * as dt from 'core/datetime';
import { apiErrorHandler } from 'core/errors';
import { DATE_FORMAT } from 'globals/constants';
import { API, getCancelToken, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import { pluralize } from 'utils';

interface GetAdminAccountsResponse extends MessageResponse {
  members: CourseTeamMember[];
}

interface SyncCourseMembersResponse extends MessageResponse {
  importStatus: 'inprogress' | 'done';
  members: CourseTeamMember[];
  removed: CourseTeamMember[];
  newAdditions: CourseTeamMember[];
}

interface TeamMembersProps extends ToastManagerProp {
  getAccess: SessionData | null;
}

interface TeamMembersState {
  loading: boolean;
  accounts: CourseTeamMember[];
  isSyncingMembers: boolean;
}

class TeamMembers extends Component<TeamMembersProps, TeamMembersState> {
  apiSources = getCancelToken();

  state: TeamMembersState = {
    loading: true,
    accounts: [],
    isSyncingMembers: false,
  };

  async getAdminAccounts(courseId: string) {
    const { members } = await request<GetAdminAccountsResponse>({
      url: API.COURSE_TEAM_MEMBERS(courseId),
      cancelToken: this.apiSources.token,
    });
    return members;
  }

  syncTeamMembers(courseId: string) {
    return request<SyncCourseMembersResponse>({
      method: 'patch',
      url: API.COURSE_MEMBERS_SYNC,
      data: { courseId },
      cancelToken: this.apiSources.token,
    });
  }

  syncData = async () => {
    const courseId = this.props.getAccess?.courseDetails.courseId;
    if (this.state.isSyncingMembers || !courseId) return;
    this.setState({ isSyncingMembers: true });

    const { removed, newAdditions } = await this.syncTeamMembers(courseId);
    const removedMemberIds = new Set(removed.map((m) => m.userId));

    this.setState((prevState) => {
      const accounts = [
        ...prevState.accounts.filter((member) => !removedMemberIds.has(member.userId)),
        ...newAdditions,
      ];
      return { accounts, isSyncingMembers: false };
    });
  };

  async componentDidMount() {
    this.apiSources = getCancelToken();
    try {
      let accounts: CourseTeamMember[] = [];
      const courseId = this.props.getAccess?.courseDetails.courseId;
      if (courseId) {
        accounts = await this.getAdminAccounts(courseId);
      }
      this.setState({ loading: false, accounts });
    } catch (err: any) {
      apiErrorHandler(this.props.toastManager, err, () => {
        this.setState({ loading: false });
      });
    }
  }

  componentWillUnmount() {
    this.apiSources.cancel();
  }

  getRoleLabel = (role: 'admin' | 'instructor' | 'ta') => {
    switch (role) {
      case 'admin':
        return 'Course Admin';
      case 'instructor':
        return 'Course Instructor';
      case 'ta':
        return 'Teaching Assistant';
      default:
        return '';
    }
  };

  getTable = () => {
    const { loading } = this.state;

    if (loading) {
      return (
        <div className='loader-wrapper'>
          <LoaderSpinner />
        </div>
      );
    }

    return (
      <table className='table table--bordered'>
        <TransitionGroup component='tbody'>
          {this.state.accounts.map((account) => (
            <CSSTransition
              key={account.userId}
              classNames='removeFromList'
              timeout={{ enter: 60, exit: 300 }}
            >
              <tr>
                <td>
                  <div className='table__cell'>
                    <div className='table__cell-header table__cell-header--title'>{account.name}</div>
                    <div className='table__cell-value'>{account.emailId}</div>
                  </div>
                </td>
                <td>
                  <div className='table__cell'>
                    <div className='table__cell-header'>JOINED ON</div>
                    <div className='table__cell-value table__cell-value--no-wrap'>
                      {account.joinedOn > 0 ? dt.format(account.joinedOn, DATE_FORMAT) : '--'}
                    </div>
                  </div>
                </td>
                <td>
                  <div className='table__cell'>
                    <div className='table__cell-header'>ROLE</div>
                    <div className='table__cell-value table__cell-value--no-wrap'>
                      {this.getRoleLabel(account.role)}
                    </div>
                  </div>
                </td>
              </tr>
            </CSSTransition>
          ))}
        </TransitionGroup>
      </table>
    );
  };

  render() {
    const { loading, accounts } = this.state;
    const activeMembers = accounts.filter((a) => a.joinedOn !== undefined && a.joinedOn > 0).length;

    return (
      <div className='section'>
        <div className='section__header'>
          <h3 className='section__title'>
            {loading
              ? 'Loading team members...'
              : !accounts.length
              ? `None of the ${accounts.length} team ${pluralize(
                  accounts.length,
                  'member'
                )} active on Acadly yet`
              : `${activeMembers} of the ${accounts.length} team ${pluralize(
                  accounts.length,
                  'member'
                )} active on Acadly`}
          </h3>
          <Button
            variant='outlined'
            color='info'
            size='large'
            className='sync-button'
            onClick={this.syncData}
          >
            {this.state.isSyncingMembers ? 'Sync in progress' : 'Sync'}
          </Button>
        </div>
        <div className='section__body'>{this.getTable()}</div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  getAccess: state.getAccess,
});

export default connect(mapStateToProps)(withToastManager(TeamMembers));
